import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Send,
  Mail,
  Phone,
  MapPin,
  Clock,
  Instagram,
  Facebook,
} from "lucide-react";
import { contactInfo } from "../data/mock";
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";

// Fonction utilitaire pour encoder les données pour Netlify
const encode = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || sent) return;
    setIsSubmitting(true);

    try {
      // Envoi direct et sécurisé à Netlify Forms
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...formData }),
      });
      if (!response.ok) throw new Error("Le message n'a pas pu être envoyé.");

      setSent(true);
      toast({
        title: "Message envoyé !",
        description: "Nous vous répondrons dans les plus brefs délais.",
      });
      
      setTimeout(() => {
        setSent(false);
        setFormData({ name: "", email: "", subject: "", message: "" });
      }, 3000);
    } catch (err) {
      console.error("Netlify Submission Error:", err);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#F6F7F1]">
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-16 bg-[#D4AF37]" />
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            <div className="h-px w-16 bg-[#D4AF37]" />
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contactez-nous
          </h1>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Une question ? Besoin d'un devis ? N'hésitez pas à nous contacter.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 space-y-8"
          >
            <div className="bg-gradient-to-br from-[#E5007D] to-[#c70069] rounded-2xl p-8 text-white">
              <h3 className="font-playfair text-2xl font-bold mb-6">
                Informations
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Email</p>
                    <p className="text-white/80 text-sm">{contactInfo.email}</p>
                  </div>
                </div>
                {contactInfo.phone && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Téléphone</p>
                    <a href={`tel:${contactInfo.phone}`} className="text-white/80 text-sm">{contactInfo.phone}</a>
                  </div>
                </div>
                )}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Adresse</p>
                    <p className="text-white/80 text-sm">
                      {contactInfo.address}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Horaires</p>
                    <p className="text-white/80 text-sm">
                      {contactInfo.hours.weekdays}
                    </p>
                    <p className="text-white/80 text-sm">
                      {contactInfo.hours.saturday}
                    </p>
                    <p className="text-white/80 text-sm">
                      {contactInfo.hours.sunday}
                    </p>
                  </div>
                </div>
              </div>

              {(contactInfo.instagram || contactInfo.facebook) && (
                <div className="mt-8 border-t border-white/20 pt-6">
                  <p className="mb-3 text-sm font-medium">Suivez-nous</p>
                  <div className="flex gap-3">
                    {contactInfo.instagram && (
                      <a href={contactInfo.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 transition-colors hover:bg-white/30">
                        <Instagram className="h-5 w-5" />
                      </a>
                    )}
                    {contactInfo.facebook && (
                      <a href={contactInfo.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 transition-colors hover:bg-white/30">
                        <Facebook className="h-5 w-5" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-3"
          >
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              onSubmit={handleSubmit}
              className="bg-[#FFF5F8] rounded-2xl p-8 space-y-6"
            >
              {/* Ligne secrète indispensable pour que Netlify repère le formulaire en AJAX */}
              <input type="hidden" name="form-name" value="contact" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#E5007D]/30 focus:border-[#E5007D] transition-all bg-white text-sm"
                    placeholder="Votre nom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#E5007D]/30 focus:border-[#E5007D] transition-all bg-white text-sm"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Sujet *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#E5007D]/30 focus:border-[#E5007D] transition-all bg-white text-sm"
                  placeholder="Objet de votre message"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#E5007D]/30 focus:border-[#E5007D] transition-all bg-white text-sm resize-none"
                  placeholder="Votre message..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || sent}
                className={`w-full py-4 rounded-full font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                  sent
                    ? "bg-green-500 text-white"
                    : "bg-[#E5007D] text-white hover:bg-[#c70069] shadow-md hover:shadow-lg hover:shadow-[#E5007D]/25"
                }`}
              >
                <Send className="w-4 h-4" />
                {isSubmitting ? "Envoi..." : sent ? "Message envoyé !" : "Envoyer le message"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
